import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Filter, 
  MapPin, 
  Calendar, 
  Users, 
  Eye, 
  Briefcase, 
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Clock,
  Building,
  IndianRupee
} from 'lucide-react';
import { jobAPI } from '@/services/api';
import clarityService from '@/services/clarityService';

interface Job {
  _id: string;
  jobId: string;
  title: string;
  companyName: string;
  location: string;
  employmentType: string;
  experienceLevel: string;
  salaryRange: {
    min: number;
    max: number;
    currency: string;
    period: string;
  };
  jobCategory: string;
  numberOfOpenings: number;
  lastDateToApply: string;
  views: number;
  applications: number;
  createdAt: string;
  postedBy: {
    firstName: string;
    lastName: string;
  };
}

interface JobsResponse {
  success: boolean;
  data: {
    jobs: Job[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalJobs: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export default function ViewJobs() {
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    location: '',
    experienceLevel: '',
    employmentType: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalJobs: 0,
    hasNext: false,
    hasPrev: false
  });

  const jobCategories = [
    'IT/Software',
    'Engineering',
    'Banking/Finance',
    'Marketing/Sales',
    'Healthcare',
    'Education',
    'Manufacturing',
    'Retail',
    'Government',
    'Non-profit',
    'Other'
  ];

  const employmentTypes = [
    'Full-time',
    'Part-time',
    'Contract',
    'Internship',
    'Freelance'
  ];

  const experienceLevels = [
    'Fresher',
    '1-2 years',
    '3-5 years',
    '6-10 years',
    '10+ years'
  ];

  const fetchJobs = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: 12,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key as keyof typeof params] === '') {
          delete params[key as keyof typeof params];
        }
      });

      const response: JobsResponse = await jobAPI.getJobs(params);

      if (response.success) {
        setJobs(response.data.jobs);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to fetch jobs');
      }
    } catch (err: any) {
      console.error('Jobs fetch error:', err);
      setError(err.response?.data?.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleFilterChange = (key: string, value: string) => {
    // Convert "all" back to empty string for backend compatibility
    const filterValue = value === 'all' ? '' : value;
    setFilters(prev => ({ ...prev, [key]: filterValue }));
  };

  const handleSearch = () => {
    // Track search event with Clarity
    clarityService.trackSearch('job', filters.search, 0); // resultsCount will be updated after fetch
    
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchJobs(1);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchJobs(page);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      location: '',
      experienceLevel: '',
      employmentType: ''
    });
    fetchJobs(1);
  };

  const formatSalary = (salaryRange: Job['salaryRange']) => {
    if (!salaryRange || (!salaryRange.min && !salaryRange.max)) {
      return 'Not disclosed';
    }
    
    const { min, max, currency = '₹', period = 'per month' } = salaryRange;
    
    // If both min and max are missing or invalid
    if (!min && !max) {
      return 'Not disclosed';
    }
    
    // If only one value is provided
    if (!min && max) {
      return `Up to ${currency} ${max.toLocaleString()} ${period}`;
    }
    if (min && !max) {
      return `From ${currency} ${min.toLocaleString()} ${period}`;
    }
    
    // If both values are the same
    if (min === max) {
      return `${currency} ${min.toLocaleString()} ${period}`;
    }
    
    // If both values are different
    return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()} ${period}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'No deadline';
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Expired';
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays <= 7) {
      return `${diffDays} days left`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getEmploymentTypeColor = (type: string) => {
    switch (type) {
      case 'Full-time':
        return 'bg-primary-light text-green-800';
      case 'Part-time':
        return 'bg-primary-light text-blue-800';
      case 'Contract':
        return 'bg-orange-100 text-orange-800';
      case 'Internship':
        return 'bg-purple-100 text-purple-800';
      case 'Freelance':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/jobs')}
                className="text-gray-600 hover:text-secondary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Jobs
              </Button>
              
              <div className="flex items-center space-x-2">
                <Briefcase className="w-6 h-6 text-primary" />
                <h1 className="text-2xl font-bold text-secondary">Browse Jobs</h1>
              </div>
            </div>
            
            <div className="text-sm text-gray-600">
              {pagination.totalJobs} job{pagination.totalJobs !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Filter className="w-5 h-5" />
              <span>Filter Jobs</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {/* Search */}
              <div className="xl:col-span-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="search"
                    placeholder="Search jobs, companies, skills..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
              </div>

              {/* Category */}
              <div className="relative">
                <Label>Category</Label>
                <Select value={filters.category || 'all'} onValueChange={(value) => handleFilterChange('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent className="z-[99999] bg-surface border shadow-lg">
                    <SelectItem value="all">All categories</SelectItem>
                    {jobCategories.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div>
                <Label>Location</Label>
                <Input
                  placeholder="City, State"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </div>

              {/* Experience Level */}
              <div className="relative">
                <Label>Experience</Label>
                <Select value={filters.experienceLevel || 'all'} onValueChange={(value) => handleFilterChange('experienceLevel', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All levels" />
                  </SelectTrigger>
                  <SelectContent className="z-[99999] bg-surface border shadow-lg">
                    <SelectItem value="all">All levels</SelectItem>
                    {experienceLevels.map(level => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Employment Type */}
              <div className="relative">
                <Label>Type</Label>
                <Select value={filters.employmentType || 'all'} onValueChange={(value) => handleFilterChange('employmentType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent className="z-[99999] bg-surface border shadow-lg">
                    <SelectItem value="all">All types</SelectItem>
                    {employmentTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <div className="flex space-x-2">
                <Button onClick={handleSearch} className="bg-primary hover:bg-primary-dark">
                  <Search className="w-4 h-4 mr-2" />
                  Search Jobs
                </Button>
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-gray-600">Loading jobs...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">{error}</div>
            <Button onClick={() => fetchJobs()} variant="outline">
              Try Again
            </Button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary mb-2">No jobs found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your filters or search terms</p>
            <Button onClick={clearFilters} variant="outline">
              Clear Filters
            </Button>
          </div>
        ) : (
          <>
            {/* Job Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {jobs.map((job) => (
                <Card key={job._id} className="hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-blue-500">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-semibold text-secondary mb-2 line-clamp-2">
                          {job.title}
                        </CardTitle>
                        <div className="flex items-center space-x-2 text-gray-600 mb-2">
                          <Building className="w-4 h-4" />
                          <span className="text-sm font-medium">{job.companyName}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-500 mb-2">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{job.location}</span>
                        </div>
                      </div>
                      <Badge className={getEmploymentTypeColor(job.employmentType)}>
                        {job.employmentType}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      {/* Salary */}
                      <div className="flex items-center space-x-2 text-primary">
                        <IndianRupee className="w-4 h-4" />
                        <span className="text-sm font-medium">{formatSalary(job.salaryRange)}</span>
                      </div>

                      {/* Experience */}
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{job.experienceLevel}</span>
                      </div>

                      {/* Category */}
                      <div>
                        <Badge variant="secondary" className="text-xs">
                          {job.jobCategory}
                        </Badge>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Eye className="w-3 h-3" />
                            <span>{job.views}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users className="w-3 h-3" />
                            <span>{job.numberOfOpenings} opening{job.numberOfOpenings !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(job.lastDateToApply)}</span>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="pt-2">
                        <Link 
                          to={`/jobs/${job._id}`}
                          onClick={() => clarityService.trackJobInteraction('view', job._id, job.title)}
                        >
                          <Button className="w-full bg-primary hover:bg-primary-dark text-white">
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                
                <div className="flex space-x-1">
                  {[...Array(pagination.totalPages)].map((_, i) => {
                    const page = i + 1;
                    const isCurrentPage = page === pagination.currentPage;
                    const isNearCurrentPage = Math.abs(page - pagination.currentPage) <= 2;
                    
                    if (pagination.totalPages <= 7 || isNearCurrentPage || page === 1 || page === pagination.totalPages) {
                      return (
                        <Button
                          key={page}
                          variant={isCurrentPage ? "default" : "outline"}
                          onClick={() => handlePageChange(page)}
                          className={isCurrentPage ? "bg-primary hover:bg-primary-dark" : ""}
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === 2 || page === pagination.totalPages - 1) {
                      return <span key={page} className="px-2">...</span>;
                    }
                    return null;
                  })}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
} 